
import { RevContainer } from '@ds/components/Container'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { parse } from 'query-string'
import { validate as validateUUID } from 'uuid'
import { mapGetters } from 'vuex'

import ToastWrapper from '@core/components/ToastWrapper/'
import { parsedHash, prepareJsonLdScript } from '@core/helpers'
import { productHomeDeepLinkBuilder } from '@core/helpers/deepLink'
import { buildProductSchema, getMetas } from '@core/helpers/head'
import {
  fetchSEOTags,
  productBreadcrumbFetch,
  productDetailsV2Fetch,
  productEcoBlockFetch,
  productGradesFetchV3,
  productPickersFetch,
  productTechnicalSpecifications,
  productVariantsV2Fetch,
  sellerDetailsFetch,
} from '@http/endpoints'
import {
  REVIEWS_TYPES,
  getGlobalRateRequest,
  getReviewsListRequest,
} from '@internal-reviews/helpers'
import logger from '@logger'
import {
  PAYMENT_ADVERTISING_TYPES,
  getAdvertisingOptionsFromComponent,
  productPaymentAdvertising,
} from '@payment'
import SatisfactionModal from '@reassurance/components/SatisfactionModal'
import TrustList from '@reassurance/components/TrustList'
import RecommendationBundle from '@recommendation/components/RecommendationBundle/RecommendationBundle.vue'
import { logReviewsError } from '@reviews/helpers'
import { ROUTES } from '@router'
import { PRODUCT_DEAL_TYPE, getProductInfos } from '@tracking'
import { trackProductPage } from '@tracking/events'

import Breadcrumb from '../components/Breadcrumb'
import EcoBlock from '../components/EcoBlock'
import IntroductionSection from '../components/IntroductionSection'
import AddToCartModal from '../components/Modals/AddToCartModal'
import OutOfStock from '../components/OutOfStock'
import PopularSearchBlock from '../components/PopularSearchBlock'
import ProductBanner from '../components/ProductBanner'
import ProductBlock from '../components/ProductBlock'
import RecommendationsBlock from '../components/RecommendationsBlock'
import ReviewsBlock from '../components/ReviewsBlock'
import { NB_REVIEWS_DISPLAYED } from '../components/ReviewsBlock/ReviewsBlock.constants'
import SeoSellerContent from '../components/SeoSellerContent'
import SocialProof from '../components/SocialProof'
import {
  SWAP_DISABLED,
  SWAP_ERROR,
  SWAP_LOADING,
  SWAP_NONE,
  SWAP_SUCCESS,
} from '../components/SwapBlock'

import translations from './product.translations'

export default {
  name: 'Product',

  components: {
    AddToCartModal,
    Breadcrumb,
    RevContainer,
    EcoBlock,
    IntroductionSection,
    PopularSearchBlock,
    ProductBanner,
    ProductBlock,
    RecommendationsBlock,
    RecommendationBundle,
    ReviewsBlock,
    SatisfactionModal,
    SeoSellerContent,
    SocialProof,
    ToastWrapper,
    TrustList,
    OutOfStock,
  },
  // TODO https://backmarket.atlassian.net/browse/FRONT-429
  // convert the default Nuxt layout to the empty one.
  layout: 'empty',

  transition({ hash }, { name } = {}) {
    if (name === ROUTES.PRODUCT.HOME && hash.includes('scroll=false')) {
      return 'none'
    }

    return 'page'
  },

  async asyncData({ store, route, error, app, redirect }) {
    const { hash, params } = route
    const { merchantId } = params
    const productId = params.uuid
    const productSlug = params.slugV2
    const routeName = merchantId ? ROUTES.PRODUCT.MERCHANT : ROUTES.PRODUCT.HOME
    const hashParsed = parsedHash(hash.substring(1))
    const l = hashParsed.l?.[0] ? parseInt(hashParsed.l[0], 10) : undefined
    const offerType = hashParsed?.offerType ?? undefined

    // A unvalid uuid leads to 404 errors.
    // If a part of the url is encoded, the uuid gets corrupted.
    // ex: c84deba7-b0dd-4dc3-977e-61cc3530a540%23l=10
    // Here we fix the uuid and we redirect with a 301.
    if (!validateUUID(productId)) {
      const uuid = productId.match(
        /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
      )

      // validateUUID is a bit stricter than our regex above… and than the validation in badoom. For instance
      // `29512ad0-ac02-7668-3c97-e2b45a4ae07a` is not valid using validateUUID but OK using our regex, due to
      // the first number of the third block. Let's avoid redirecting to the same page and breaking everything.
      if (uuid !== null && productId !== uuid[0]) {
        logger.info('Product unvalid uuid redirection')

        return redirect(301, {
          name: routeName,
          params: {
            ...params,
            uuid: uuid[0],
          },
        })
      }
    }

    // Those calls are non blocking, meaning that the page MUST be rendered even if any of them is failing,
    // resulting in missing blocks in the page (but the user will still be able to see the product and add it to
    // the cart).
    // Note that those calls also don't handle the rematching feature (a product that isn't sold anymore but
    // redirected to another product), so all of them will result in a 404 if the product is rematched. We could avoid
    // this by calling the product_v2 endpoint before, but with the cost of increasing the page load.
    const nonBlockingRequests = [
      store
        .dispatch('http/request', {
          request: productBreadcrumbFetch,
          pathParams: { id: productId },
        })
        .catch(() => ({ payload: [] })),

      store
        .dispatch('http/request', {
          request: fetchSEOTags,
          queryParams: {
            model: 'product',
            pk: productId,
          },
        })
        .catch(() => ({ payload: [] })),

      store
        .dispatch('http/request', {
          request: productTechnicalSpecifications,
          pathParams: { id: productId },
        })
        .catch(() => ({ payload: {} })),

      store
        .dispatch('http/request', {
          request: productEcoBlockFetch,
          pathParams: { productId },
        })
        .catch(() => ({ payload: { display: false } })),
    ]

    // Either use the "legacy" variants endpoint or the new pickers one. This is currently driven via
    // a feature flag to control the rollout of the feature.
    // TODO [BRO-2655] Remove variants endpoint call
    const shouldUsePickersEndpoint = (
      app.$config?.FF_PRODUCT_PICKERS ?? ''
    ).includes(store.getters['config/country'])
    if (!shouldUsePickersEndpoint) {
      nonBlockingRequests.push(
        store
          .dispatch('http/request', {
            request: productVariantsV2Fetch,
            pathParams: { id: productId },
            queryParams: {
              ...(merchantId && { merchant: merchantId }),
              ...(offerType && { special_offer_type: offerType }),
              // [SR-2591] Remove the popular master picker experiment
              'experiment.popularMasterPicker':
                store.getters['flags/getExperiments']?.popularMasterPicker,
            },
          })
          .catch(() => ({ payload: {} })),
      )
      nonBlockingRequests.push(Promise.resolve({ payload: { pickers: [] } }))
    } else {
      nonBlockingRequests.push(Promise.resolve({ payload: {} }))
      nonBlockingRequests.push(
        store
          .dispatch('http/request', {
            request: productPickersFetch,
            pathParams: { id: productId },
            queryParams: {
              ...(l && { grade: l }),
              ...(offerType && { special_offer_type: offerType }),
            },
          })
          .catch(() => ({ payload: { pickers: [] } })),
      )
    }

    if (app.$config.KILL_REVIEW_PRODUCT_PAGE === true) {
      nonBlockingRequests.push(
        Promise.resolve({ payload: {} }),
        Promise.resolve({
          payload: [],
        }),
      )
    } else {
      nonBlockingRequests.push(
        store
          .dispatch(
            'http/request',
            getGlobalRateRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: productId },
            }),
          )
          .catch((err) => {
            logReviewsError({ tags: ['ProductPage'], err })

            return { payload: {} }
          }),
        store
          .dispatch(
            'http/request',
            getReviewsListRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: productId },
              route,
              limit: NB_REVIEWS_DISPLAYED,
            }),
          )
          .catch((err) => {
            logReviewsError({ tags: ['ProductPage'], err })

            return { payload: {} }
          }),
      )
    }

    try {
      const blockingRequests = [
        store.dispatch('http/request', {
          request: productGradesFetchV3,
          pathParams: { id: productId },
          queryParams: {
            ...(merchantId && { merchant: merchantId }),
            offer_type: offerType,
          },
        }),

        store.dispatch('http/request', {
          request: productDetailsV2Fetch,
          pathParams: { id: productId },
        }),
      ]

      const [
        { payload: grades },
        { payload: product },
        { payload: breads },
        { payload: tags },
        { payload: technicalSpecifications },
        { payload: ecoBlock },
        { payload: variants },
        { payload: pickers },
        { payload: ratings },
        { payload: reviews },
      ] = await Promise.all([...blockingRequests, ...nonBlockingRequests])

      const fetchedId = product.link.params.uuid.toString()
      const fetchedSlug = product.link.params.slugV2

      const merchantName = merchantId
        ? (
            await store.dispatch('http/request', {
              request: sellerDetailsFetch,
              pathParams: { id: merchantId },
            })
          ).payload.link.params.merchantName
        : null

      if (
        merchantId &&
        (merchantName !== params.merchantName || productId !== fetchedId)
      ) {
        logger.info('Product merchant or product id redirection')

        return redirect(301, {
          name: routeName,
          params: {
            locale: params.locale,
            uuid: fetchedId,
            merchantName,
            merchantId,
          },
        })
      }

      // If ProductV2 endpoint has rematched the requested product to a different product,
      // we redirect to this rematched product instead.
      // /!\ Only product_v2 and best_offers_V3 endpoints handles the rematch feature, as it's quite
      // heavy on the database. All the other calls will results in a 404.
      if (
        isEmpty(merchantId) &&
        (productSlug !== fetchedSlug || productId !== fetchedId)
      ) {
        logger.info('Product rematch redirection')

        return redirect(301, {
          name: routeName,
          params: {
            locale: params.locale,
            uuid: fetchedId,
            slugV2: fetchedSlug,
          },
        })
      }

      const selectedByDefault = grades.find(
        (grade) => grade.isSelectedByDefault,
      )

      const doesGradeExist = grades.some(
        (grade) => grade.backboxGrade.value === l && !grade.isDisabled,
      )

      const activeBackboxGrade = doesGradeExist
        ? l
        : selectedByDefault && selectedByDefault.backboxGrade.value

      return {
        activeBackboxGrade,
        breads,
        colors: variants.variantsByDeviceOptions?.color ?? [],
        grades,
        highlightedPickers: variants.recommendedVariants ?? [],
        product,
        productId,
        productSlug: fetchedSlug,
        ratings,
        reviews: reviews?.results || [],
        shouldUpdateGradeInURL: !doesGradeExist,
        storages: variants.variantsByDeviceOptions?.storage ?? [],
        tags,
        technicalSpecifications,
        pickers: pickers.pickers,
        ecoBlock,
      }
    } catch (err) {
      return error({
        statusCode: err.status_code,
        message: app.$t(translations.productPageNotFoundError),
      })
    }
  },

  data() {
    return {
      activeBackboxGrade: null,
      breads: [],
      colors: [],
      grades: [],
      hasSwapFailed: false,
      highlightedPickers: [],
      isSwapLoading: true,
      product: {},
      productId: '',
      ratings: {},
      redirectWithNewGrade: false,
      reviews: [],
      shouldUpdateGradeInURL: false,
      storages: [],
      tags: [],
      technicalSpecifications: {},
      pickers: [],
      productAlternative: {},
      additionalListings: [],
      ecoBlock: { display: false },
    }
  },

  head() {
    const { images, trackingDetails } = this.product

    const title = this.productName

    const description = this.metaDescription

    const image = (images[0] || {}).url

    const path = this.canonicalPath

    const { brand } = trackingDetails

    let productPrice = {}
    if (!isEmpty(this.activeGrade)) {
      // TODO GNL-2480 add a kill switch for priceWithServiceFee usage
      // price = get(
      //   this.activeGrade,
      //   'fee.priceWithServiceFee',
      //   this.activeGrade.price,
      // )

      productPrice = this.activeGrade.price
    }

    const isRatesAvailable =
      !isEmpty(this.ratings) &&
      this.ratings.averageRate > 0 &&
      this.ratings.reviewsCount > 0

    const alternateLinks = Object.values(this.product.links).map(
      ({ href, params: { locale } }) => ({
        rel: 'alternate',
        hreflang: locale,
        href,
      }),
    )

    const productSchema = buildProductSchema({
      title,
      description,
      rate: isRatesAvailable ? this.ratings : {},
      image,
      brand,
      price: productPrice,
      isOutOfStock: this.isOutOfStock,
    })

    return {
      title: this.$t(translations.metaTitle, { title }),
      meta: [
        ...getMetas({
          title,
          description,
          image,
          'og:type': 'product',
        }),
      ],
      link: [
        ...alternateLinks,
        {
          href: path,
          rel: 'canonical',
        },
      ],
      script: prepareJsonLdScript({ productSchema }),
      __dangerouslyDisableSanitizers: ['script'],
    }
  },

  computed: {
    modelTitle() {
      return this.product.title
    },
    listingId() {
      return this.offers.activeGrade.id
    },
    accessories() {
      return !isEmpty(this.technicalSpecifications)
        ? this.technicalSpecifications.comesWith.items ?? []
        : []
    },
    isBundleExperimentOn() {
      return (
        this.experiments.crossSellBundle === 'CrossSellWithBundle1ProductAbove'
      )
    },
    bundleTitle() {
      return this.$t(translations.bundleTitle)
    },
    ADVERTISING_PRODUCT: () => PAYMENT_ADVERTISING_TYPES.PRODUCT,

    ...mapGetters({
      baseURL: 'config/baseURL',
      coolingOffDays: 'config/coolingOffDays',
      country: 'config/country',
      experiments: 'flags/getExperiments',
      isSwapEnabled: 'swap/isSwapEnabled',
      swapEstimations: 'swap/estimations',
      warrantyDuration: 'config/warrantyDuration',
    }),

    translations: () => translations,

    metaDescription() {
      const { model, trackingDetails } = this.product

      return this.$t(translations.metaDescription, {
        title: this.productName,
        warrantyDuration: this.warrantyDuration,
        coolingOffDays: this.coolingOffDays,
        category: trackingDetails.category3Name,
        brand: trackingDetails.brand,
        model,
      })
    },

    canonicalPath() {
      const { params } = this.$route
      /**
       * We add a link `rel canonical` to the product page on all pages.
       * On pages variations such as seller product pages or other special offer types,
       * it makes sure that there is no SEO duplicate content
       *  */

      const productRoute = this.$navigation.resolve(
        {
          name: ROUTES.PRODUCT.HOME,
          params: {
            ...(params.locale && { locale: params.locale }),
            slugV2: this.productSlug,
            uuid: this.productId,
          },
        },
        /**
         * By default, vue-router's resolve method will also use the current route
         * to resolve the route we want. To avoid unexpected results, let's force
         * it to resolve the route with only the params we have
         */
        {},
      )

      return `${this.baseURL}${productRoute.resolved.path}`
    },

    activeGrade() {
      return (
        this.grades.find(
          (grade) => grade.backboxGrade.value === this.activeBackboxGrade,
        ) || {}
      )
    },

    swapListings() {
      return (
        this.offers.grades &&
        this.offers.grades.reduce((availableListings, grade) => {
          if (grade && grade.id) {
            availableListings.push(grade.id)
          }

          return availableListings
        }, [])
      )
    },

    highlightedPickersWithIsSelected() {
      const { hash, params } = this.$route
      const hashParsed = parsedHash(hash.substring(1))
      const l = hashParsed.l?.[0] ? parseInt(hashParsed.l[0], 10) : undefined
      const productId = params.uuid

      return this.highlightedPickers.map((picker) => {
        const pickerProductId = picker.link.params.uuid
        const pickerGradeId = parseInt(picker.link.hash.l, 10)
        const isGradeHighlighted =
          l === pickerGradeId ||
          (l === undefined && pickerGradeId === this.activeBackboxGrade)

        return {
          ...picker,
          isSelected: productId === pickerProductId && isGradeHighlighted,
          link: {
            ...picker.link,
            hash: {
              ...picker.link.hash,
              scroll: false,
            },
          },
        }
      })
    },

    // TODO: [BRO-1841] should be used for highlightedPicker selection
    selectedHighlightedPickers() {
      return this.highlightedPickersWithIsSelected.reduce((acc, picker) => {
        if (picker.isSelected) {
          return picker
        }

        return acc
      }, null)
    },

    offers() {
      return {
        activeBackboxGrade: this.activeBackboxGrade,
        activeGrade: this.activeGrade,
        colors: this.colors,
        grades: this.grades,
        highlightedPickers: this.highlightedPickersWithIsSelected,
        selectedHighlightedPickers: this.selectedHighlightedPickers,
        storages: this.storages,
      }
    },

    productContainerClasses() {
      return [
        'bg-white text-black max-w-full mb-7',
        { 'pt-4 md:pt-6': !this.breads.length },
      ]
    },

    hasTags() {
      return !isEmpty(this.tags)
    },

    hasSellerContent() {
      return !isEmpty(this.product.description)
    },

    shouldDisplayEcoBlock() {
      return this.ecoBlock.display
    },

    productFromInfos() {
      return getProductInfos(this.product.legacyId)
    },

    productName() {
      const productSubtitle = this.product.subTitleElements || []

      return [this.product.title, productSubtitle.join(' - ')].join(' ')
    },

    stock() {
      return this.activeGrade?.reassurance?.stock?.stockRawValue || 0
    },

    productTrackingInfo() {
      let color = null

      if (!isEmpty(this.colors)) {
        color = this.colors.find((item) => item.selected)?.name
      } else if (!isEmpty(this.pickers)) {
        color =
          this.pickers
            .find((group) => group.id === 'color')
            ?.items.find((picker) => picker.selected)?.label || null
      }

      const merchantId =
        !isEmpty(this.activeGrade) && this.activeGrade.merchant
          ? String(this.activeGrade.merchant.id)
          : ''

      const { advertised } = productPaymentAdvertising(
        get(this.activeGrade, 'paymentMethods', []),
        getAdvertisingOptionsFromComponent(this),
      )

      const advertisedPaymentMethodIds =
        get(advertised, 'methods', [])
          .map((method) => method.bmCode)
          .join('_') || null

      const dealType = this.selectedHighlightedPickers
        ? this.selectedHighlightedPickers.id
        : PRODUCT_DEAL_TYPE.NORMAL

      const image = this.product.images?.[0] ?? {}
      const webUrl = this.product.link?.href
      const deepLink = productHomeDeepLinkBuilder({ $route: this.$route })
      const mobileDeeplink = `backmarket://${deepLink}`
      const hasMobilePlan = Boolean(this.product.mobilePlan)
      const specialOfferType = parse(this.$route.hash).offerType || '0'

      return {
        averageRate: this.ratings.averageRate,
        brand: this.product.trackingDetails.brand,
        category: this.product.trackingDetails.category3Name,
        color,
        currency: !isEmpty(this.activeGrade)
          ? this.activeGrade.price.currency
          : null,
        dealType,
        hasMobilePlan,
        hasReviews: this.areReviewsDisplayed,
        id: Number(this.product.legacyId),
        image: image.url,
        isSwapEnabled: this.isSwapEligible,
        list: this.productFromInfos.source,
        listingId: !isEmpty(this.activeGrade)
          ? String(this.activeGrade.id)
          : null,
        merchantId,
        mobileDeeplink,
        model: this.product.model,
        name: this.productName,
        numberReviewsDisplayed: this.reviewsList.length,
        numberTotalReviews: this.ratings.reviewsCount,
        price: !isEmpty(this.activeGrade)
          ? this.activeGrade.price.amount
          : null,
        splitPayment: advertisedPaymentMethodIds,
        stock: this.stock,
        uuid: this.product.id,
        variant: !isEmpty(this.activeGrade)
          ? this.activeGrade.backboxGrade.value
          : null,
        webUrl,
        shippingDelay:
          this.activeGrade.reassurance?.shipping?.free?.delayInHours ?? 0,
        hasExpressShipping: !!this.activeGrade.reassurance?.shipping?.express,
        hasNewBattery: ['disabled', 'available'].includes(
          this.activeGrade?.newBattery?.status,
        ),
        hasNewBatteryInStock:
          this.activeGrade?.newBattery?.status === 'available',
        specialOfferType,
      }
    },

    listingsTrackingInfos() {
      return this.grades.map(({ id, backboxGrade = {}, price = {} }) => ({
        id,
        variant: backboxGrade.value,
        price: price.amount,
      }))
    },

    swapOffer() {
      return this.swapEstimations.find(
        (estimation) => estimation.id === this.offers.activeGrade.id,
      )
    },

    isSwapInCart() {
      return this.swapOffer?.isOffer
    },

    isSwapEligible() {
      return this.product.isSwapEligible
    },

    noSwapHaveBeenEstimated() {
      return (
        !isEmpty(this.swapOffer) && this.isSwapEnabled && !this.isSwapInCart
      )
    },

    swapStatus() {
      if (!this.isSwapEligible) {
        return SWAP_DISABLED
      }

      if (this.isSwapLoading) {
        return SWAP_LOADING
      }

      if (this.hasSwapFailed) {
        return SWAP_ERROR
      }

      if (this.noSwapHaveBeenEstimated) {
        return SWAP_NONE
      }

      return SWAP_SUCCESS
    },

    areReviewsDisplayed() {
      return this.ratings.reviewsCount > 0
    },

    landingReviewsLink() {
      if (this.ratings.reviewsCount < 1) {
        return {}
      }

      const route = {
        name: ROUTES.REVIEWS.PAGES.PRODUCT,
        params: {
          productId: this.productId,
        },
        query: {
          productName: this.product.title,
        },
      }

      return route
    },
    reviewsList() {
      return this.reviews.slice(0, NB_REVIEWS_DISPLAYED)
    },
    isOutOfStock() {
      const { grades } = this.offers

      return grades.every((grade) => grade.isDisabled)
    },
  },

  watch: {
    async productId() {
      await this.fetchListingData()
    },

    '$route.hash': async function onHashChange(newHash, prevHash) {
      await this.fetchListingData()
      await this.refreshPickers()

      const prevOfferType = parse(prevHash).offerType ?? undefined
      const newOfferType = parse(newHash).offerType ?? undefined

      if (newOfferType !== prevOfferType) {
        await this.refreshBestOffers()
        // This is for the legacy pickers
        await this.refreshVariants()
      }
    },

    '$route.params': function onParamsChange(currentParams, previousParams) {
      if (currentParams.id !== previousParams.id) {
        this.$nuxt.refresh()
      }
    },

    '$route.query': {
      async handler() {
        try {
          const { payload: reviews } = await this.$store.dispatch(
            'http/request',
            getReviewsListRequest({
              type: REVIEWS_TYPES.PRODUCT,
              params: { id: this.$route.params.uuid },
              route: this.$route,
              limit: NB_REVIEWS_DISPLAYED,
            }),
          )
          this.reviews = reviews?.results || []
        } catch (err) {
          logReviewsError({ tags: ['ProductPage', 'Filter'], err })
        }
      },
    },
  },

  updated() {
    const hash = parsedHash(this.$route.hash)

    if (
      this.shouldUpdateGradeInURL &&
      hash.l?.[0] &&
      parseInt(hash.l?.[0], 10) !== this.activeBackboxGrade
    ) {
      this.$navigation.replace({
        type: 'internal',
        name: this.$route.name,
        params: this.$route.params,
        query: this.$route.query,
        hash: {
          ...hash,
          l: this.activeBackboxGrade,
        },
      })
    }
    this.shouldUpdateGradeInURL = false
  },

  async mounted() {
    await this.fetchListingData()
  },

  methods: {
    async refreshBestOffers() {
      const { merchantId } = this.$route.params
      const { offerType } = parse(this.$route.hash)

      try {
        const { payload } = await this.$store.dispatch('http/request', {
          request: productGradesFetchV3,
          pathParams: { id: this.productId },
          queryParams: {
            ...(merchantId && { merchant: merchantId }),
            ...(offerType && { offer_type: offerType }),
          },
        })

        this.grades = payload
      } catch (error) {
        logger.error('[PRODUCT] Error refreshing best offers', { error })
      }
    },

    async refreshVariants() {
      const shouldUsePickersEndpoint = (
        this.$config?.FF_PRODUCT_PICKERS ?? ''
      ).includes(this.country)

      if (shouldUsePickersEndpoint) {
        return
      }

      const { merchantId } = this.$route.params
      const { offerType } = parse(this.$route.hash)

      try {
        const { payload } = await this.$store.dispatch('http/request', {
          request: productVariantsV2Fetch,
          pathParams: { id: this.productId },
          queryParams: {
            ...(merchantId && { merchant: merchantId }),
            ...(offerType && { special_offer_type: offerType }),
            // [SR-2591] Remove the popular master picker experiment
            'experiment.popularMasterPicker':
              this.$store.getters['flags/getExperiments']?.popularMasterPicker,
          },
        })

        this.colors = payload.variantsByDeviceOptions?.color ?? []
        this.highlightedPickers = payload.recommendedVariants ?? []
        this.storages = payload.variantsByDeviceOptions?.storage ?? []
      } catch (error) {
        logger.error('[PRODUCT] Error refreshing variants', { error })
      }
    },

    async refreshPickers() {
      const shouldUsePickersEndpoint = (
        this.$config?.FF_PRODUCT_PICKERS ?? ''
      ).includes(this.country)

      if (!shouldUsePickersEndpoint) {
        return
      }

      const { offerType } = parse(this.$route.hash)

      try {
        const { payload } = await this.$store.dispatch('http/request', {
          request: productPickersFetch,
          pathParams: { id: this.productId },
          queryParams: {
            grade: this.activeBackboxGrade,
            ...(offerType && { special_offer_type: offerType }),
          },
        })

        this.pickers = payload.pickers
      } catch (error) {
        logger.error('[PRODUCT] Error refreshing pickers', { error })
      }
    },

    onAddToCartModalClose() {
      this.additionalListings = []
    },
    onRecommendationBundleAddToCart({ additionalListings }) {
      this.additionalListings = additionalListings
    },
    onRecommendationsLoaded(results) {
      this.productAlternative = results?.length
        ? { product: results[0] }
        : {
            landing: this.breads.slice(-2, -1)[0],
          }
    },
    async fetchListingData() {
      if (parse(this.$route.hash).l) {
        this.setActiveBackboxGrade(parse(this.$route.hash).l)
      }

      if (this.isSwapEligible) {
        try {
          await this.$store.dispatch(
            'swap/getSwapEstimation',
            this.swapListings,
          )
          this.hasSwapFailed = false
        } catch {
          this.hasSwapFailed = true
        }
      }
      this.isSwapLoading = false

      this.logDesync()
      trackProductPage({
        product: this.productTrackingInfo,
        listings: this.listingsTrackingInfos,
      })
    },
    setActiveBackboxGrade(newBackboxGrade) {
      const backboxGradeInteger = parseInt(newBackboxGrade, 10)

      const doesGradeExist = this.grades.some(
        (grade) =>
          grade.backboxGrade.value === backboxGradeInteger && !grade.isDisabled,
      )

      if (doesGradeExist && backboxGradeInteger !== this.activeBackboxGrade) {
        this.activeBackboxGrade = backboxGradeInteger
      }
    },
    logPriceDesync() {
      if (!isEmpty(this.activeGrade)) {
        const { price, source } = this.productFromInfos
        const priceProductCard = Number(price)
        const actualPrice = Number(this.activeGrade.price.amount)

        if (actualPrice !== priceProductCard) {
          logger.info('[S&R][Error][ProductPage] Price desync detected', {
            id: this.$route.params.uuid,
            cardPrice: priceProductCard,
            productPrice: actualPrice,
            country: this.country,
            source,
          })
        }
      }
    },
    logStockDesync() {
      if (this.isOutOfStock) {
        const { source } = this.productFromInfos

        logger.info('[S&R][Error][ProductPage] Stock desync detected', {
          id: this.$route.params.uuid,
          country: this.country,
          source,
        })
      }
    },
    logDesync() {
      const { price, source } = this.productFromInfos

      // When opening a link in a new tab or window, a new session storage instance is created.
      // In our case, the product thumbnail price will not be found. It means that we can't
      // properly know wether prices are desync or not, so we skip the monitoring step.
      // See: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API
      if (price === undefined || source === undefined) {
        return
      }

      this.logPriceDesync()
      this.logStockDesync()
    },
  },
}
